/**
 * Pagination utility functions optimized for Supabase
 *
 * These utilities follow Supabase best practices for efficient pagination:
 * - Cursor-based (keyset) pagination for better performance
 * - Estimated counts for large datasets
 * - Efficient filtering with indexed columns
 */

import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Builds a cursor-based pagination query for Supabase
 *
 * @example
 * const query = buildCursorQuery({
 *   supabase,
 *   table: 'content',
 *   orderBy: 'created_at',
 *   orderDirection: 'desc',
 *   pageSize: 10,
 *   cursor: { orderValue: '2025-01-01', id: 'abc123' }
 * });
 */
export function buildCursorQuery({
  supabase,
  table,
  select = '*',
  orderBy,
  orderDirection = 'desc',
  pageSize,
  cursor,
  filters,
}: {
  supabase: SupabaseClient;
  table: string;
  select?: string;
  orderBy: string;
  orderDirection?: 'asc' | 'desc';
  pageSize: number;
  cursor?: { orderValue: unknown; id: string } | null;
  filters?: (query: ReturnType<ReturnType<SupabaseClient['from']>['select']>) => ReturnType<ReturnType<SupabaseClient['from']>['select']>;
}) {
  let query = supabase.from(table).select(select);

  // Apply custom filters
  if (filters) {
    // TypeScript has issues with the complex Supabase filter types, but functionally this works
     
    query = filters(query as any) as any;
  }

  // Apply cursor-based filtering
  if (cursor) {
    // Format the cursor value - wrap strings (including timestamps) in double quotes for PostgREST
    const formattedValue = typeof cursor.orderValue === 'string'
      ? `"${cursor.orderValue.replace(/"/g, '\\"')}"` // Escape quotes and wrap in quotes
      : cursor.orderValue;

    if (orderDirection === 'desc') {
      // For descending order: WHERE (orderBy < cursor.orderValue) OR (orderBy = cursor.orderValue AND id < cursor.id)
      query = query.or(
        `${orderBy}.lt.${formattedValue},and(${orderBy}.eq.${formattedValue},id.lt.${cursor.id})`
      );
    } else {
      // For ascending order: WHERE (orderBy > cursor.orderValue) OR (orderBy = cursor.orderValue AND id > cursor.id)
      query = query.or(
        `${orderBy}.gt.${formattedValue},and(${orderBy}.eq.${formattedValue},id.gt.${cursor.id})`
      );
    }
  }

  // Apply ordering with id as tie-breaker
  query = query
    .order(orderBy, { ascending: orderDirection === 'asc' })
    .order('id', { ascending: orderDirection === 'asc' })
    .limit(pageSize + 1); // Fetch one extra to check for more pages

  return query;
}

/**
 * Get total count with optional estimation for large datasets
 *
 * For tables with millions of rows, exact counts can be slow.
 * Use estimated counts for better performance.
 *
 * @example
 * const count = await getTotalCount({
 *   supabase,
 *   table: 'content',
 *   filters: (q) => q.eq('status', 'published'),
 *   useEstimate: true // Use for large tables
 * });
 */
export async function getTotalCount({
  supabase,
  table,
  filters,
  useEstimate = false,
}: {
  supabase: SupabaseClient;
  table: string;
  filters?: (query: ReturnType<ReturnType<SupabaseClient['from']>['select']>) => ReturnType<ReturnType<SupabaseClient['from']>['select']>;
  useEstimate?: boolean;
}): Promise<number> {
  let query = supabase.from(table).select('*', {
    count: useEstimate ? 'estimated' : 'exact',
    head: true,
  });

  if (filters) {
    query = filters(query);
  }

  const { count } = await query;
  return count || 0;
}

/**
 * Extract cursor from a data item (internal helper)
 */
function extractCursor<T extends { id: string; [key: string]: unknown }>(
  item: T | undefined,
  orderBy: keyof T
): { orderValue: T[keyof T]; id: string } | null {
  if (!item) return null;

  return {
    orderValue: item[orderBy],
    id: item.id,
  };
}

/**
 * Calculate page info from cursor pagination results
 *
 * @example
 * const { hasMore, nextCursor } = getPageInfo(items, pageSize, 'created_at');
 */
export function getPageInfo<T extends { id: string; [key: string]: unknown }>(
  items: T[],
  pageSize: number,
  orderBy: keyof T
): {
  hasMore: boolean;
  nextCursor: { orderValue: T[keyof T]; id: string } | null;
  prevCursor: { orderValue: T[keyof T]; id: string } | null;
} {
  const hasMore = items.length > pageSize;
  const pageData = hasMore ? items.slice(0, pageSize) : items;

  return {
    hasMore,
    nextCursor: pageData.length > 0 ? extractCursor(pageData[pageData.length - 1], orderBy) : null,
    prevCursor: pageData.length > 0 ? extractCursor(pageData[0], orderBy) : null,
  };
}
